import { FunctionComponent, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Dispatch, RootState } from "core/store/store.types";
import {
  Avatar,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { MetaLogin } from "components/organisms/meta-login";
import { Text } from "components/atoms/text";
import { reportAsync, reportSelectors } from "core/store/modules/report";
import { Report } from "core/types/report.types";
import { FacebookUser } from "core/types/facebook-user.types";
import {
  facebookUserAsync,
  facebookUserSelectors,
} from "core/store/modules/facebook-user";
import i18n from "i18n";
import { ReportDetailPage } from "pages/report-detail";
import {
  socialActions,
  socialAsync,
  socialSelectors,
} from "core/store/modules/social";
import { SocialReportDetail } from "core/types/social.types";
import { EmptyState } from "components/atoms/empty-state";
import useSessionStorageWatcher from "core/hooks/useSessionStorageWatcher";
import FB from "../../assets/svg/fb.svg";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

interface OwnProps {}
interface StateProps {
  reportList?: Report[];
  isReportLoading: boolean;
  facebookUser?: FacebookUser;
  reportDetail?: SocialReportDetail;
  isSocialReportLoading: boolean;
  isReportReady: boolean;
}
interface DispatchProps {
  getSocialReportList: () => void;
  saveFacebookInfo: (userInfo: any) => void;
  getFacebookInfo: () => void;
  getReportDetail: (fileId: number) => void;
  resetIsReportReady: () => void;
}

export type Props = OwnProps & StateProps & DispatchProps;

const MySocialReports: FunctionComponent<Props> = ({
  reportDetail,
  reportList,
  isReportLoading,
  facebookUser,
  isReportReady,
  isSocialReportLoading,
  getSocialReportList,
  saveFacebookInfo,
  getFacebookInfo,
  getReportDetail,
  resetIsReportReady,
}) => {
  const { t } = useTranslation();
  const [selectedReport, setSelectedReport] = useState<Report>();
  const [auth, setAuth] = useState<boolean>(false);
  const sessionData = useSessionStorageWatcher("Meta_Token");
  const [orderedList, setOrderedList] = useState<Report[]>();
  const linguaCorrente = i18n.language;

  useEffect(() => {
    getSocialReportList();
  }, [sessionData]);

  useEffect(() => {
    if (reportList) {
      setOrderedList(
        reportList.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
      );
    }
  }, [reportList]);

  useEffect(() => {
    if (auth) {
      getFacebookInfo();
    }
  }, [auth]);

  const handleMetaLogout = () => {
    sessionStorage.removeItem("Meta_Token");
    sessionStorage.removeItem("Facebook_User");
  };

  useEffect(() => {
    if (selectedReport) {
      resetIsReportReady();
      getReportDetail(selectedReport.fileId);
    }
  }, [selectedReport]);

  useEffect(() => {
    setAuth(sessionData ? true : false);
  }, [sessionData]);

  return (
    <>
      <Grid container display={"flex"} justifyContent={"center"}>
        {auth ? (
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
              ml={"20px"}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    position: "relative",
                  }}
                >
                  <Avatar
                    alt={facebookUser?.name}
                    src={facebookUser?.picture.data.url}
                  />
                  <div
                    style={{
                      position: "absolute",
                      right: 0,
                      bottom: "-5px",
                    }}
                  >
                    <img src={FB} width={"17px"} height={"17px"} />
                  </div>
                </div>
                <Text ml={"10px"}>{facebookUser?.name}</Text>
              </div>
              <Button variant="contained" onClick={() => handleMetaLogout()}>
                {t("drawer-dashboard.logout")}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Paper
                sx={{
                  borderRadius: "10px",
                  padding: "20px",
                }}
              >
                <FormControl
                  fullWidth
                  variant="standard"
                  sx={{ marginBottom: "26px" }}
                >
                  <InputLabel
                    id="accounts-label"
                    sx={{ fontSize: "14px !important", fontWeight: "700" }}
                  >
                    {t("report.title")}
                  </InputLabel>
                  <Select
                    labelId="accounts-label"
                    id="accounts-select"
                    value={selectedReport ? selectedReport.fileId : undefined}
                    MenuProps={MenuProps}
                    color="secondary"
                    renderValue={(value) => <Text>{value}</Text>}
                  >
                    {isReportLoading ? (
                      <MenuItem
                        disabled
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <CircularProgress size={24} />
                      </MenuItem>
                    ) : (
                      orderedList?.map((r) => (
                        <MenuItem
                          key={r.fileId}
                          value={r.fileId}
                          onClick={() => setSelectedReport(r)}
                        >
                          <ListItem>
                            <ListItemText
                              primary={r.fileId}
                              secondary={new Date(
                                r.createdAt
                              ).toLocaleDateString(
                                linguaCorrente === "en" ? "en-GB" : "it-IT",
                                {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                }
                              )}
                            />
                          </ListItem>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                <Grid item xs={12} display={"inline-block"} width={"100%"}>
                  {selectedReport ? (
                    <>
                      {isSocialReportLoading ? (
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <CircularProgress color="primary" size={"50px"} />
                        </div>
                      ) : isReportReady ? (
                        reportDetail && (
                          <ReportDetailPage
                            id={selectedReport?.fileId}
                            reportDetail={reportDetail}
                          />
                        )
                      ) : (
                        <>
                          <Text
                            fontSize={18}
                            fontWeight={700}
                            textAlign={"center"}
                          >
                            {t("empty_states.no_report.title")}
                          </Text>
                          <Text
                            fontSize={15}
                            fontWeight={500}
                            textAlign={"center"}
                          >
                            {t("empty_states.no_report.subtitle")}
                          </Text>
                        </>
                      )}
                    </>
                  ) : (
                    <EmptyState
                      title={t("empty_states.empty_report.title")}
                      description={t("empty_states.empty_report.subtitle")}
                    />
                  )}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <MetaLogin setAuth={setAuth} onFacebookCallback={saveFacebookInfo} />
        )}
      </Grid>
    </>
  );
};

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  (state: RootState) => ({
    reportDetail: socialSelectors.getReportDetail(state),
    reportList: reportSelectors.getReportList(state),
    isReportLoading: reportSelectors.isReportLoading(state),
    facebookUser: facebookUserSelectors.getFacebookUser(state),
    isReportReady: socialSelectors.isReportReady(state),
    isSocialReportLoading: socialSelectors.isSocialReportLoading(state),
  }),
  (dispatch: Dispatch) => ({
    getSocialReportList: () => dispatch(reportAsync.getAllSocialReports()),
    saveFacebookInfo: (userInfo: any) =>
      dispatch(facebookUserAsync.saveFacebookInfo(userInfo)),
    getFacebookInfo: () => dispatch(facebookUserAsync.getFacebookInfo()),
    getReportDetail: (fileId: number) =>
      dispatch(socialAsync.getReportDetail(fileId)),
    resetIsReportReady: () =>
      dispatch(socialActions.isReportReady({ isReportReady: false })),
  })
)(MySocialReports);
